import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Edit2, Trash2, Plus, RefreshCw, FolderPlus } from 'lucide-react';
import menuApi from 'api/requests/menu';
import BottomSheet from 'components/_UI/BottomSheet';
import CategoryForm from '../forms/CategoryForm';
import ConfirmDialog from '../components/ConfirmDialog';
import colors from 'theme/colors';

export default forwardRef(function CategoriesTab({ slug, toast, onCategoriesChange }, ref) {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sheet, setSheet] = useState({ open: false, data: null });
    const [confirm, setConfirm] = useState({ open: false, id: null, name: '' });
    const [deleting, setDeleting] = useState(false);

    useImperativeHandle(ref, () => ({
        openCreate() { setSheet({ open: true, data: null }); }
    }));

    useEffect(() => { fetchCategories(); }, []);

    async function fetchCategories() {
        setLoading(true);
        setError(null);
        try {
            const { data: res } = await menuApi.getCategories(slug);
            setCategories(res.data || []);
        } catch {
            setError('Não foi possível carregar as categorias.');
        } finally {
            setLoading(false);
        }
    }

    function requestDelete(cat) {
        setConfirm({ open: true, id: cat.id, name: cat.name });
    }

    async function handleConfirmDelete() {
        setDeleting(true);
        try {
            await menuApi.deleteCategory(slug, confirm.id);
            setCategories(prev => {
                const next = prev.filter(c => c.id !== confirm.id);
                onCategoriesChange?.(next);
                return next;
            });
            toast?.success('Categoria removida com sucesso');
        } catch {
            toast?.error('Erro ao remover categoria');
        } finally {
            setDeleting(false);
            setConfirm({ open: false, id: null, name: '' });
        }
    }

    function handleSaved(saved) {
        const isEdit = categories.find(c => c.id === saved.id);
        setCategories(prev => {
            const exists = prev.find(c => c.id === saved.id);
            const next = exists
                ? prev.map(c => c.id === saved.id ? saved : c)
                : [...prev, saved];
            onCategoriesChange?.(next);
            return next;
        });
        toast?.success(isEdit ? 'Categoria atualizada' : 'Categoria criada com sucesso');
        setSheet({ open: false, data: null });
    }

    if (loading) return <LoadingContainer><Spinner /><LoadingText>Carregando categorias...</LoadingText></LoadingContainer>;

    if (error) {
        return (
            <ErrorState>
                <ErrorIcon><RefreshCw size={24} color={colors.danger} /></ErrorIcon>
                <ErrorTitle>{error}</ErrorTitle>
                <RetryBtn onClick={fetchCategories} aria-label='Tentar novamente'>
                    <RefreshCw size={14} /> Tentar novamente
                </RetryBtn>
            </ErrorState>
        );
    }

    return (
        <Container>
            {categories.length === 0 && (
                <EmptyState>
                    <EmptyIconWrapper><FolderPlus size={32} color={colors.occasionalPurple} /></EmptyIconWrapper>
                    <EmptyText>Nenhuma categoria criada</EmptyText>
                    <EmptyHint>Categorias organizam os produtos do seu cardápio</EmptyHint>
                    <EmptyCTA onClick={() => setSheet({ open: true, data: null })}>
                        <Plus size={16} /> Criar primeira categoria
                    </EmptyCTA>
                </EmptyState>
            )}

            {categories.map(cat => (
                <CategoryCard key={cat.id}>
                    <CardLeft>
                        <CategoryName>{cat.name}</CategoryName>
                        {cat.description && <CategoryDesc>{cat.description}</CategoryDesc>}
                    </CardLeft>
                    <CardActions>
                        <ActionBtn
                            onClick={() => setSheet({ open: true, data: cat })}
                            aria-label={`Editar categoria ${cat.name}`}
                        >
                            <Edit2 size={16} color={colors.night} />
                        </ActionBtn>
                        <ActionBtn
                            onClick={() => requestDelete(cat)}
                            aria-label={`Remover categoria ${cat.name}`}
                        >
                            <Trash2 size={16} color={colors.danger} />
                        </ActionBtn>
                    </CardActions>
                </CategoryCard>
            ))}

            <BottomSheet
                isOpen={sheet.open}
                onClose={() => setSheet({ open: false, data: null })}
                title={sheet.data ? 'Editar categoria' : 'Nova categoria'}
            >
                <CategoryForm
                    slug={slug}
                    initialData={sheet.data}
                    onSaved={handleSaved}
                    onCancel={() => setSheet({ open: false, data: null })}
                />
            </BottomSheet>

            <ConfirmDialog
                isOpen={confirm.open}
                title='Remover categoria'
                message={`Tem certeza que deseja remover "${confirm.name}"? Esta ação não pode ser desfeita.`}
                confirmLabel='Remover'
                danger
                loading={deleting}
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirm({ open: false, id: null, name: '' })}
            />
        </Container>
    );
});

const Container = styled.div`
    padding: 20px 16px 24px;
    position: relative;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const CategoryCard = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    border: 1px solid ${colors.neutralDark};
    transition: box-shadow 0.15s;
    &:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.12); }
`;

const CardLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

const CategoryName = styled.span`
    font-weight: 700;
    font-size: 15px;
    color: ${colors.nightDark};
`;

const CategoryDesc = styled.span`
    font-size: 13px;
    color: #666;
`;

const CardActions = styled.div`
    display: flex;
    gap: 8px;
    margin-left: 12px;
`;

const ActionBtn = styled.button`
    background: none;
    border: none;
    padding: 6px;
    cursor: pointer;
    border-radius: 6px;
    display: flex;
    align-items: center;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: 2px; }
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    gap: 12px;
`;

const LoadingText = styled.span`
    font-size: 13px;
    color: #666;
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled.div`
    width: 32px; height: 32px;
    border: 3px solid ${colors.neutralDark};
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
`;

const EmptyIconWrapper = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: ${colors.occasionalPurple}10;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const EmptyText = styled.p`font-weight: 700; font-size: 16px; color: ${colors.nightDark}; margin: 0 0 6px;`;
const EmptyHint = styled.p`font-size: 13px; color: #666; margin: 0 0 18px;`;

const EmptyCTA = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;

const ErrorState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    gap: 8px;
`;

const ErrorIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${colors.danger}10;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
`;

const ErrorTitle = styled.p`
    font-weight: 600;
    font-size: 15px;
    color: ${colors.nightDark};
    margin: 0;
`;

const RetryBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 8px;
    background: none;
    border: 1.5px solid ${colors.neutralDark};
    color: ${colors.nightDark};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 8px;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
`;
